import { Box, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { LinkButton } from '@/components/form/baseInputs/Link/LinkButton';
import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { ArrowNarrowRightIcon } from '@/components/icons/ArrowNarrowRightIcon';
import { COLORS } from '@/styles/tokens/colors';

export interface WarningBannerProps {
  title: ReactNode;
  description?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
}

/** A full-width warning strip. Callers own visibility and action permissions. */
export function WarningBanner({
  title,
  description,
  action,
}: WarningBannerProps) {
  return (
    <Stack
      role="status"
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={1.5}
      sx={{
        bgcolor: COLORS.FUNCTIONAL.WARNING[50],
        px: 2,
        py: 0.5,
        minHeight: (theme) => theme.spacing(4.5),
        flexShrink: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flex="1 1 auto"
        minWidth={0}
      >
        <AlertTriangleFilledIcon
          size={16}
          color={COLORS.FUNCTIONAL.WARNING[500]}
          aria-hidden
        />
        <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
          <Box component="span" sx={{ typography: 'title-xs' }}>
            {title}
          </Box>
          {description && <> {description}</>}
        </Typography>
      </Stack>
      {action && (
        <LinkButton
          display={action.label}
          underline={false}
          endIcon={<ArrowNarrowRightIcon size={16} aria-hidden />}
          onClick={action.onClick}
          disabled={action.disabled}
          sx={{
            flexShrink: 0,
            ml: 'auto',
            '&:hover': { backgroundColor: 'transparent' },
            '&:hover:not(:disabled) span': { textDecoration: 'underline' },
            '&:disabled': { opacity: 0.5, pointerEvents: 'none' },
          }}
        />
      )}
    </Stack>
  );
}
